import {Placement} from '@popperjs/core';
import React, {FC, ReactNode, RefObject, useRef, useState} from 'react';
import styled from 'styled-components';

import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {PreviewReposition} from '../../../../../libs/shared/ui/src/components/reposition/previewReposition';
import {shadows} from '../../../../../libs/shared/ui/src/styles/colorStyles';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';

/*
 * Constants.
 */

interface TriggerPreviewProps {
  anchor: RefObject<HTMLElement>;
  popperPlacement: Placement;
  header: ReactNode;
  content: ReactNode;
  footer?: ReactNode;
  optionsDropdownRenderer?: (menuRef: RefObject<HTMLDivElement>, onDismiss: () => void) => ReactNode;
  anchorRef?: RefObject<HTMLDivElement>;
}

/*
 * Styles.
 */

const StyledPreviewDiv = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: ${(p) => p.theme.foreground};
  overflow: hidden;
  ${shadows.dialogsAndDropdowns};
  width: 270px;
  max-height: 60vh;

  @media screen and (max-height: 1000px) {
    max-height: 90vh;
  }
`;
const IconButtonWrapperDiv = styled.div``;

const StyledHeaderDiv = styled.header`
  ${flexCenter}
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
  color: ${(p) => p.theme.message.header};
  border-bottom: 1px solid ${(p) => p.theme.button.border};
  padding: 14px 16px;
  > ${IconButtonWrapperDiv}:last-child {
    margin-left: auto;
  }
`;

const StyledFooter = styled.footer`
  margin: 16px;
  margin-top: 8px;
`;

const StyledContentDiv = styled.div`
  padding: 16px;
  padding-bottom: 8px;
  overflow-y: auto;
  flex-shrink: 1;
  flex-grow: 1;
  flex-basis: auto;
`;

/*
 * Component.
 */

export const TriggerPreview: FC<TriggerPreviewProps> = ({
  anchor,
  popperPlacement,
  header,
  content,
  footer,
  optionsDropdownRenderer,
}) => {
  const [shouldShowMenu, setShouldShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const hasDropdown = isDefined(optionsDropdownRenderer);
  return (
    <PreviewReposition anchor={anchor} popperPlacement={popperPlacement}>
      <StyledPreviewDiv>
        <StyledHeaderDiv>
          {header}
          {hasDropdown && (
            <IconButtonWrapperDiv>
              <IconButton
                iconName="moreVerticalSmall"
                label={null}
                ref={menuRef}
                onClick={(event) => {
                  event.stopPropagation();
                  setShouldShowMenu(true);
                }}
                size={VisualSizesEnum.SMALL}
              />
            </IconButtonWrapperDiv>
          )}
          {shouldShowMenu && hasDropdown && optionsDropdownRenderer(menuRef, () => setShouldShowMenu(false))}
        </StyledHeaderDiv>
        <StyledContentDiv>{content}</StyledContentDiv>
        {footer && <StyledFooter>{footer}</StyledFooter>}
      </StyledPreviewDiv>
    </PreviewReposition>
  );
};
